/**
 * Data Layer - Error Handling
 * Handles API error processing and normalization
 *
 * NOTE: Error messages here are translation keys, not user-facing text.
 * The presentation layer should use these keys with translations.
 */

import { AxiosError } from "axios";
import { siteConfig } from "@/config/site";
import { HTTP_STATUS } from "@/constants";
import {
  ApiError,
  AuthError,
  ForbiddenError,
  NetworkError,
  ServerError,
  ValidationError,
} from "@/shared/core/errors";

/**
 * Standardized error response structure
 */
export interface ErrorResponse {
  message: string;
  statusCode?: number;
  validationErrors?: Record<string, string[]>;
  isNetworkError: boolean;
  isAuthError: boolean;
  isValidationError: boolean;
  isServerError: boolean;
}

/**
 * Extended error interface with status information
 */
export interface ErrorWithStatus extends Error {
  status?: number;
  statusCode?: number;
  isValidationError?: boolean;
  isAuthError?: boolean;
}

/**
 * Extract error message from various response structures
 */
const getErrorMessage = (error: AxiosError, defaultMessage: string): string => {
  const responseData = error.response?.data as
    | Record<string, unknown>
    | undefined;

  // Handle nested error object: { error: { message: "..." } }
  if (responseData?.error && typeof responseData.error === "object") {
    const errorObj = responseData.error as { message?: string };
    if (errorObj.message) return errorObj.message;
  }
  // Handle message field: { message: "..." }
  if (responseData?.message && typeof responseData.message === "string") {
    return responseData.message;
  }
  // Handle detail field (FastAPI/Python backends): { detail: "..." }
  if (responseData?.detail) {
    if (typeof responseData.detail === "string") {
      return responseData.detail;
    }
    // Handle array of validation errors: { detail: [{ msg: "..." }, ...] }
    if (Array.isArray(responseData.detail)) {
      const messages = responseData.detail
        .map((e: { msg?: string; message?: string }) => e.msg || e.message)
        .filter(Boolean);
      if (messages.length > 0) return messages.join(", ");
    }
  }
  // Handle plain string response
  if (typeof responseData === "string") {
    return responseData;
  }
  return defaultMessage;
};

/**
 * Error handler for processing API errors
 */
export const errorHandler = {
  /**
   * Handle any error and return a standardized error response
   */
  handle(error: unknown): ErrorResponse {
    // Log detailed error information in development mode
    if (!siteConfig.isProduction) {
      console.group("🔴 API Error Details");

      if (error instanceof AxiosError) {
        console.log("📍 Endpoint:", error.config?.url);
        console.log("🔢 Status Code:", error.response?.status);
        console.log("📦 Response Data:", error.response?.data);
        console.log("📋 Full Error:", error);
      } else {
        console.log("📋 Error:", error);
      }

      console.groupEnd();
    }

    // Check for offline status
    if (typeof window !== "undefined" && !window.navigator.onLine) {
      return {
        message: "errors.network.offline",
        isNetworkError: true,
        isAuthError: false,
        isValidationError: false,
        isServerError: false,
      };
    }

    // Handle Axios errors
    if (error instanceof AxiosError) {
      return this.handleAxiosError(error);
    }

    // Handle custom errors
    if (error instanceof ApiError) {
      return {
        message: error.message,
        statusCode: error.statusCode,
        isNetworkError: false,
        isAuthError: false,
        isValidationError: false,
        isServerError: false,
      };
    }

    if (error instanceof NetworkError) {
      return {
        message: error.message,
        isNetworkError: true,
        isAuthError: false,
        isValidationError: false,
        isServerError: false,
      };
    }

    if (error instanceof AuthError) {
      return {
        message: error.message,
        statusCode: error.statusCode,
        isNetworkError: false,
        isAuthError: true,
        isValidationError: false,
        isServerError: false,
      };
    }

    if (error instanceof ForbiddenError) {
      return {
        message: error.message,
        statusCode: error.statusCode,
        isNetworkError: false,
        isAuthError: false,
        isValidationError: false,
        isServerError: false,
      };
    }

    if (error instanceof ValidationError) {
      return {
        message: error.message,
        statusCode: error.statusCode,
        validationErrors: error.errors,
        isNetworkError: false,
        isAuthError: false,
        isValidationError: true,
        isServerError: false,
      };
    }

    if (error instanceof ServerError) {
      return {
        message: error.message,
        statusCode: error.statusCode,
        isNetworkError: false,
        isAuthError: false,
        isValidationError: false,
        isServerError: true,
      };
    }

    // Handle standard Error objects
    if (error instanceof Error) {
      return {
        message: error.message || "errors.unexpected",
        isNetworkError: false,
        isAuthError: false,
        isValidationError: false,
        isServerError: false,
      };
    }

    // Handle unknown errors
    return {
      message: String(error) || "errors.unknown",
      isNetworkError: false,
      isAuthError: false,
      isValidationError: false,
      isServerError: false,
    };
  },

  /**
   * Handle Axios-specific errors
   */
  handleAxiosError(error: AxiosError): ErrorResponse {
    const statusCode = error.response?.status;

    // Handle network errors (no status code)
    if (!statusCode) {
      return {
        message: "errors.network.unableToConnect",
        isNetworkError: true,
        isAuthError: false,
        isValidationError: false,
        isServerError: false,
      };
    }

    // Handle bad request errors (400)
    if (statusCode === HTTP_STATUS.BAD_REQUEST) {
      return {
        message: getErrorMessage(error, "errors.request.badRequest"),
        statusCode,
        isNetworkError: false,
        isAuthError: false,
        isValidationError: false,
        isServerError: false,
      };
    }

    // Handle unauthorized errors (401)
    if (statusCode === HTTP_STATUS.UNAUTHORIZED) {
      return {
        message: getErrorMessage(error, "errors.auth.loginAgain"),
        statusCode,
        isNetworkError: false,
        isAuthError: true,
        isValidationError: false,
        isServerError: false,
      };
    }

    // Handle forbidden errors (403)
    if (statusCode === HTTP_STATUS.FORBIDDEN) {
      return {
        message: getErrorMessage(error, "errors.auth.noPermission"),
        statusCode,
        isNetworkError: false,
        isAuthError: false,
        isValidationError: false,
        isServerError: false,
      };
    }

    // Handle not found errors (404)
    if (statusCode === HTTP_STATUS.NOT_FOUND) {
      return {
        message: getErrorMessage(error, "errors.resource.notFound"),
        statusCode,
        isNetworkError: false,
        isAuthError: false,
        isValidationError: false,
        isServerError: false,
      };
    }

    // Handle validation errors (422)
    if (statusCode === HTTP_STATUS.UNPROCESSABLE_ENTITY) {
      const responseData = error.response?.data as
        | { errors?: Record<string, string[]> }
        | undefined;
      return {
        message: getErrorMessage(error, "errors.validation.checkInput"),
        statusCode,
        validationErrors: responseData?.errors,
        isNetworkError: false,
        isAuthError: false,
        isValidationError: true,
        isServerError: false,
      };
    }

    // Handle server errors (5xx)
    if (statusCode >= HTTP_STATUS.INTERNAL_SERVER_ERROR) {
      return {
        message: getErrorMessage(error, "errors.server.unexpected"),
        statusCode,
        isNetworkError: false,
        isAuthError: false,
        isValidationError: false,
        isServerError: true,
      };
    }

    // Handle other HTTP errors
    return {
      message: getErrorMessage(error, error.message || "errors.unexpected"),
      statusCode,
      isNetworkError: false,
      isAuthError: false,
      isValidationError: false,
      isServerError: false,
    };
  },
};

/**
 * Legacy class export for backward compatibility
 */
export class ErrorHandler {
  static handle(error: unknown): ErrorResponse {
    return errorHandler.handle(error);
  }
}

export default errorHandler;
